import axios from 'axios';
import { toast } from 'sonner';
import logger from '../utils/logger';

/**
 * usePeticionesHandlers
 *
 * Handlers de la pestaña "Peticiones": reordenar (drag-and-drop), eliminar y
 * ocultar/mostrar al cliente (modo sorpresa).
 *
 * Jun-2026 (auditoría D-01): todas las escrituras son atómicas por petición
 * (reorder por ids, DELETE por id, PATCH por id). Nunca se envía el array
 * completo, así no se pisan las peticiones que entran en paralelo (invitados
 * live, asistente, parser de email/documentos).
 *
 * @param {Object}   params
 * @param {string}   params.API                 — base URL del backend
 * @param {Object}   params.currentPlaylist     — evento actual (usa `id` y `peticiones`)
 * @param {Function} params.setCurrentPlaylist  — setter del contexto de playlists
 * @param {Function} params.refetchPlaylists    — refresca el listado desde backend (fallback en errores)
 */
export function usePeticionesHandlers({
  API,
  currentPlaylist,
  setCurrentPlaylist,
  refetchPlaylists,
}) {
  const applyServerPeticiones = (peticiones) => {
    if (Array.isArray(peticiones)) {
      setCurrentPlaylist({ ...currentPlaylist, peticiones });
    }
  };

  // Drag-and-drop: reordena peticiones con actualización optimista + persistencia por ids.
  // `bucketFilter` (opcional): cuando el drag ocurre sobre una lista FILTRADA
  // (ej. subpestaña "Banquete" de Peticiones, donde los índices del resultado
  // son posiciones dentro del subconjunto visible, no del array completo),
  // se reordena SOLO ese subconjunto y se reinserta en las mismas posiciones
  // que ocupaba dentro del array completo, sin tocar el resto de peticiones.
  const handlePeticionesDragEnd = async (result, bucketFilter) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    try {
      const peticiones = currentPlaylist.peticiones || [];
      let items;
      if (typeof bucketFilter === 'function') {
        const positions = [];
        const subset = [];
        peticiones.forEach((p, i) => {
          if (bucketFilter(p)) { positions.push(i); subset.push(p); }
        });
        const reorderedSubset = Array.from(subset);
        const [moved] = reorderedSubset.splice(result.source.index, 1);
        reorderedSubset.splice(result.destination.index, 0, moved);
        items = Array.from(peticiones);
        positions.forEach((pos, i) => { items[pos] = reorderedSubset[i]; });
      } else {
        items = Array.from(peticiones);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
      }

      setCurrentPlaylist({ ...currentPlaylist, peticiones: items });

      const response = await axios.post(`${API}/playlists/${currentPlaylist.id}/peticiones/reorder`, {
        ordered_ids: items.map((p) => p.id).filter(Boolean),
      });
      // El servidor devuelve el orden definitivo (incluye peticiones nuevas de otros)
      applyServerPeticiones(response.data?.peticiones);

      toast.success('Orden actualizado');
      logger.log('✅ [DRAG & DROP] Peticiones reordered successfully');
    } catch (error) {
      console.error('❌ [DRAG & DROP] Error reordering peticiones:', error);
      toast.error(error.response?.status === 409
        ? 'El evento cambió mientras reordenabas, se ha recargado'
        : 'Error al reordenar peticiones');
      await refetchPlaylists();
    }
  };

  // Elimina una petición del evento por id (el índice solo sirve para localizarla en la UI)
  const handleRemovePeticion = async (playlistId, peticionIndex) => {
    const peticion = (currentPlaylist.peticiones || [])[peticionIndex];
    if (!peticion?.id) {
      toast.error('No se pudo identificar la petición');
      return;
    }
    try {
      await axios.delete(`${API}/playlists/${playlistId}/peticiones/${peticion.id}`);

      setCurrentPlaylist({
        ...currentPlaylist,
        peticiones: (currentPlaylist.peticiones || []).filter((p) => p.id !== peticion.id),
      });
      await refetchPlaylists();

      toast.success('Petición eliminada');
    } catch (error) {
      console.error('Error removing peticion:', error);
      toast.error('Error al eliminar petición');
    }
  };

  // Alterna la visibilidad de la petición para el cliente (modo sorpresa)
  const handleTogglePeticionHidden = async (playlistId, peticionIndex) => {
    const peticion = (currentPlaylist.peticiones || [])[peticionIndex];
    if (!peticion?.id) {
      toast.error('No se pudo identificar la petición');
      return;
    }
    const isNowHidden = !peticion.hidden_from_client;
    try {
      await axios.patch(`${API}/playlists/${playlistId}/peticiones/${peticion.id}`, {
        hidden_from_client: isNowHidden,
      });

      setCurrentPlaylist({
        ...currentPlaylist,
        peticiones: (currentPlaylist.peticiones || []).map((p) =>
          p.id === peticion.id ? { ...p, hidden_from_client: isNowHidden } : p
        ),
      });

      toast.success(
        isNowHidden
          ? '🎁 Petición ocultada al cliente (sorpresa)'
          : '👁 Petición visible para el cliente'
      );
    } catch (error) {
      console.error('Error toggling peticion visibility:', error);
      toast.error('Error al cambiar visibilidad');
    }
  };

  return {
    handlePeticionesDragEnd,
    handleRemovePeticion,
    handleTogglePeticionHidden,
  };
}
